import axios from 'axios';

// Pre-seeded cache for known WordPress media IDs to avoid redundant network requests
const mediaCache = new Map([
    [172, 'https://emr.metadei.in/wp-content/uploads/2024/12/enable_service.png'],
    [170, 'https://emr.metadei.in/wp-content/uploads/2024/12/mobile_service.png'],
    [171, 'https://emr.metadei.in/wp-content/uploads/2024/12/aiml_service.png'],
    [560, 'https://emr.metadei.in/wp-content/uploads/2026/09/ChatGPT-Image-Sep-26-2026-01_42_37-AM.png'],
    [168, 'https://emr.metadei.in/wp-content/uploads/2024/12/vision.png'],
    [166, 'https://emr.metadei.in/wp-content/uploads/2024/12/mission.jpg'],
    [167, 'https://emr.metadei.in/wp-content/uploads/2024/12/value.jpg'],
    [184, 'https://emr.metadei.in/wp-content/uploads/2024/12/gunadhar_logo-1.png'],
    [181, 'https://emr.metadei.in/wp-content/uploads/2024/12/rishab_logo.png'],
    [222, 'https://emr.metadei.in/wp-content/uploads/2025/01/pps-log.png'],
    [224, 'https://emr.metadei.in/wp-content/uploads/2025/01/Srfti_logo-2.png'],
    [228, 'https://emr.metadei.in/wp-content/uploads/2025/01/emdee_logo_rectangular.png'],
]);

/**
 * Normalizes an image URL (ensuring HTTPS)
 */
export const normalizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url || '';
    return url.replace(/^http:\/\/emr\.metadei\.in/, 'https://emr.metadei.in');
};

/**
 * Resolves a single image field (could be an ID number, object, or URL string).
 * Returns a Promise that resolves to the image URL string, or fallback.
 */
export const getMediaUrl = async (imageField, fallback = '') => {
    if (!imageField) return fallback;

    // If it's already an object with url
    if (typeof imageField === 'object' && imageField.url) {
        return normalizeImageUrl(imageField.url);
    }

    // If it's a URL string
    if (typeof imageField === 'string' && (imageField.startsWith('http') || imageField.startsWith('/') || imageField.startsWith('data:'))) {
        return normalizeImageUrl(imageField);
    }

    // If it's a numeric ID (or numeric string)
    const numericId = Number(imageField);
    if (!isNaN(numericId) && numericId > 0) {
        if (mediaCache.has(numericId)) {
            return mediaCache.get(numericId);
        }
        try {
            const res = await axios.get(`https://emr.metadei.in/wp-json/wp/v2/media/${numericId}`);
            const url = normalizeImageUrl(res.data?.source_url || res.data?.guid?.rendered || '');
            if (url) {
                mediaCache.set(numericId, url);
                return url;
            }
        } catch (e) {
            console.error(`Failed to fetch media ID ${numericId}:`, e);
        }
    }

    return fallback;
};

/**
 * Batch resolves a list of media IDs to a map: { [id]: url }
 */
export const fetchMediaMap = async (ids = []) => {
    const uniqueIds = [...new Set(ids)]
        .map(id => Number(id))
        .filter(id => !isNaN(id) && id > 0);

    const neededIds = uniqueIds.filter(id => !mediaCache.has(id));

    if (neededIds.length > 0) {
        await Promise.all(
            neededIds.map(async (id) => {
                try {
                    const res = await axios.get(`https://emr.metadei.in/wp-json/wp/v2/media/${id}`);
                    const url = normalizeImageUrl(res.data?.source_url || res.data?.guid?.rendered || '');
                    if (url) {
                        mediaCache.set(id, url);
                    }
                } catch (e) {
                    console.error(`Failed to fetch media ID ${id}:`, e);
                }
            })
        );
    }

    const result = {};
    uniqueIds.forEach(id => {
        result[id] = mediaCache.get(id) || null;
    });
    return result;
};
